import React, { useState, useEffect, useCallback } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import * as Sentry from '@sentry/react'

import { TwilioRoom, JitsiRoom } from '~/views/CallRoom'
import { errorTypes } from '~/views/ErrorView/Errors'
import FaceId from '~/views/FaceId'
import { views } from '~/views/viewsCode'
import WaitingRoom from '~/views/WaitingRoom'

import SettingsModal from '~/components/SettingsModal'
import Spinner from '~/components/Spinner'

import { DevicesProvider } from '../../contexts/DevicesContext'

import { dateTimeHandler, fetchApi, browserDetectionHandler } from '~/handlers'
import { useSchedule } from '~/hooks'
import { openReplay } from '~/modules/OpenReplay'
import { axios, endpoints } from '~/settings'

const { twilio, jitsi } = browserDetectionHandler()

const MeetingView = () => {
  const { hash } = useParams()
  const history = useHistory()

  const [view, setView] = useState(views.waitingRoom)
  const [accessToken, setAccessToken] = useState(null)
  const [meeting, setMeeting] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [showSettingsModal, setShowSettingsModal] = useState(false)
  const { setSchedule } = useSchedule()

  const updateMeeting = (data) => {
    setMeeting(prevMeeting => ({ ...prevMeeting, ...data }))
    setSchedule(prevSchedule => ({ ...prevSchedule, ...data }))
  }

  const videoProvider = meeting?.schedule?.videoProvider || 'TWILIO' // Fallback para Twilio

  const handleAuthentication = useCallback(async () => {
    if (!hash || hash === 'null') {
      setError({ type: 'generic', code: 'ROOM_NOT_FOUND' })
      setIsLoading(false)
      return
    }

    try {
      const response = await axios.get(endpoints.virtualRooms.AUTH(hash))
      const { schedule, user, guest, patient_preferences } = response.data.data
      const { timestamp } = response.headers

      const endDate = schedule.end_date
      const serverTime = dateTimeHandler.getDateFromTimestamp(timestamp)
      const serverDuration = dateTimeHandler.getDurationInMs(serverTime, endDate) || 0
      const localDuration = dateTimeHandler.getDurationInMs(null, endDate) || 0
      const diffDurationToNormalize = serverDuration - localDuration
      const detectedVideoProvider = schedule.video_provider || 'TWILIO'
      const meetingData = {
        guest,
        user,
        patientPreferences: patient_preferences,
        schedule: {
          id: schedule.id,
          hash: schedule.virtual_room_hash,
          startDate: schedule.start_date,
          endDate: schedule.end_date,
          diffDuration: diffDurationToNormalize,
          interval: schedule.procedure.interval,
          guests: schedule.guests,
          planCode: schedule.plan_code,
          planGroupName: schedule.plan_group_name,
          planGroupCode: schedule.plan_group_code,
          tokenHasBeenFilled: schedule.token_has_been_filled,
          videoProvider: detectedVideoProvider,
          allowsAutomaticSchedulingViaReservation: schedule.allows_automatic_scheduling_via_reservation
        },
      }

      if (detectedVideoProvider === 'TWILIO') {
        const isSupported = twilio.isSupportedBrowser && twilio.isSupportedVersion
        if (!isSupported) {
          return history.replace({
            pathname: 'error',
            state: { errorType: errorTypes.BROWSER },
          })
        }
      }

      if (detectedVideoProvider === 'JITSI') {
        const isSupported = jitsi.isSupportedBrowser
        if (!isSupported) {
          return history.replace({
            pathname: 'error',
            state: { errorType: errorTypes.BROWSER },
          })
        }
      }

      updateMeeting(meetingData)
      Sentry.configureScope(scope => scope.setUser({ email: user.email }))

      openReplay.identifyUser(user, {
        video_provider: detectedVideoProvider,
        schedule_id: schedule.id,
        virtual_room_hash: schedule.virtual_room_hash,
        plan_code: schedule.plan_code,
        plan_group_code: schedule.plan_group_code,
      })

      fetchApi.sendLogToServer(schedule.virtual_room_hash, {
        event: 'GUEST_CONNECTED',
        is_supported: detectedVideoProvider === 'TWILIO' ? twilio.isSupportedBrowser : jitsi.isSupportedBrowser,
      })
    } catch (err) {
      const handleErrorByStatusCode = {
        401: () => setError({ type: 'generic', code: 'ERROR_AUTH' }),
        403: () => setView(views.faceId),
        404: () => setError({ type: 'generic', code: 'ROOM_NOT_FOUND' }),
        410: () => history.replace({
          pathname: 'error',
          state: { errorType: errorTypes.TIME_UP },
        }),
      }
      if (err && handleErrorByStatusCode[err.status]) {
        handleErrorByStatusCode[err.status]()
      } else {
        setError({ type: 'generic', code: 'UNKNOWN_ERROR' })
      }
    } finally {
      setIsLoading(false)
    }
  }, [hash, history, setSchedule])

  useEffect(() => {
    handleAuthentication()
  }, [handleAuthentication])

  function handleShowSettingsModal() {
    setShowSettingsModal(true)
  }

  function handleCloseSettingsModal() {
    setShowSettingsModal(false)
  }

  if (isLoading) {
    return <Spinner />
  }

  if (view === views.faceId) {
    return <FaceId />
  }

  if (!meeting && !error) {
    return <Spinner />
  }

  const CallContent = (
    <>
      {view === views.waitingRoom && (
        <WaitingRoom
          hash={hash}
          updateView={setView}
          accessToken={accessToken}
          updateAccessToken={setAccessToken}
          meeting={meeting}
          updateMeeting={updateMeeting}
          onOpenSettingsModal={handleShowSettingsModal}
          error={error}
        />
      )}
      {view === views.callRoom && videoProvider === 'TWILIO' && (
        <TwilioRoom
          updateView={setView}
          accessToken={accessToken}
          meeting={meeting}
          onOpenSettingsModal={handleShowSettingsModal}
        />
      )}

      {view === views.callRoom && videoProvider === 'JITSI' && (
        <JitsiRoom
          meeting={meeting}
          onOpenSettingsModal={handleShowSettingsModal}
        />
      )}

      {showSettingsModal && (
        <SettingsModal onClose={handleCloseSettingsModal} />
      )}
    </>
  )

  return <DevicesProvider>{CallContent}</DevicesProvider>
}

export default MeetingView
